import { useState, useEffect } from 'react'
import {
  Plus, MoreVertical, RefreshCw, UserX, UserCheck, Trash2, MapPin, Search, Building2,
  List, LayoutGrid, Network, ChevronLeft, ChevronRight, X, Copy, Check,
} from 'lucide-react'
import CreateUserModal from './CreateUserModal'
import AssignBranchModal from './AssignBranchModal'
import UsersTable from './UsersTable'
import RoleOrganogram from './RoleOrganogram'
import { useUserDirectory, useUserCounts } from '../../hooks/useUserDirectory'
import { ROLE_LABELS, ROLE_COLORS, getInitials, CopyablePassword, DeleteConfirm } from './userShared'

const PAGE_SIZE = 50

function UserCard({ user, currentUser, organizations, branches, onDeactivate, onReactivate, onResetPassword, onDelete, onAssign, canManage, isSuperAdmin }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [newTempPassword, setNewTempPassword] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const org = organizations.find((o) => o.id === user.organizationId)
  const branch = branches.find((b) => b.id === user.branchId)
  const isSelf = user.id === currentUser.id

  async function handleReset() {
    setMenuOpen(false)
    const result = await onResetPassword(user.id)
    if (result?.tempPassword) setNewTempPassword(result.tempPassword)
  }

  function handleDeactivate() {
    setMenuOpen(false)
    onDeactivate(user.id)
  }

  function handleReactivate() {
    setMenuOpen(false)
    onReactivate(user.id)
  }

  function handleDelete() {
    setMenuOpen(false)
    setConfirmDelete(true)
  }

  return (
    <div className={`bg-white rounded-xl border px-4 py-3 ${user.isActive ? 'border-gray-100' : 'border-gray-100 opacity-60'}`}>
      <div className="flex items-start space-x-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
          user.isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
        }`}>
          {getInitials(user.name)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
            <p className="font-semibold text-sm text-gray-800 truncate">{user.name}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${ROLE_COLORS[user.role]}`}>
              {ROLE_LABELS[user.role]}
            </span>
            {!user.isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Inactive</span>
            )}
            {user.mustChangePassword && user.isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-600 font-medium">Password pending</span>
            )}
            {['manager', 'staff'].includes(user.role) && !user.branchId && user.isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">No branch</span>
            )}
          </div>
          <p className="text-xs text-gray-400 font-mono mt-0.5">@{user.username}</p>
          {user.email && <p className="text-xs text-gray-500 mt-0.5 truncate">{user.email}</p>}
          {org && <p className="text-xs text-gray-400 mt-0.5 truncate">{org.name}{branch ? ` · ${branch.name}` : ''}</p>}
        </div>

        {canManage && !isSelf && (
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-9 z-20 bg-white rounded-xl shadow-xl border border-gray-100 w-44 py-1 overflow-hidden">
                  {user.role !== 'superadmin' && (
                    <button
                      onClick={() => { setMenuOpen(false); onAssign(user) }}
                      className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                    >
                      <MapPin className="w-4 h-4 text-teal-600" />
                      <span>Assign Branch</span>
                    </button>
                  )}
                  <button
                    onClick={handleReset}
                    className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <RefreshCw className="w-4 h-4 text-blue-500" />
                    <span>Reset Password</span>
                  </button>
                  {user.isActive ? (
                    <button
                      onClick={handleDeactivate}
                      className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-red-600 hover:bg-red-50 transition"
                    >
                      <UserX className="w-4 h-4" />
                      <span>Deactivate</span>
                    </button>
                  ) : (
                    <button
                      onClick={handleReactivate}
                      className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-green-600 hover:bg-green-50 transition"
                    >
                      <UserCheck className="w-4 h-4" />
                      <span>Reactivate</span>
                    </button>
                  )}
                  {isSuperAdmin && (
                    <>
                      <div className="h-px bg-gray-100 mx-2 my-1" />
                      <button
                        onClick={handleDelete}
                        className="w-full flex items-center space-x-2.5 px-3.5 py-2.5 text-sm text-red-600 hover:bg-red-50 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete Account</span>
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {newTempPassword && (
        <CopyablePassword
          password={newTempPassword}
          onDismiss={() => setNewTempPassword(null)}
        />
      )}

      {confirmDelete && (
        <DeleteConfirm user={user} onDelete={onDelete} onClose={() => setConfirmDelete(false)} />
      )}
    </div>
  )
}

// Temp passwords produced by a bulk reset — shown as one copyable block since
// there is nowhere else for the operator to retrieve them afterwards.
function BulkPasswords({ entries, onDismiss }) {
  const [copied, setCopied] = useState(false)
  const text = entries.map((e) => `${e.username}: ${e.tempPassword}`).join('\n')
  function copy() {
    navigator.clipboard?.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-3">
      <div className="flex items-center gap-2">
        <p className="flex-1 text-xs font-semibold text-amber-700">
          {entries.length} temp password{entries.length !== 1 ? 's' : ''} — copy them now, they are not shown again
        </p>
        <button onClick={copy} className="text-amber-600" aria-label="Copy all passwords">
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        </button>
        <button onClick={onDismiss} className="text-amber-400" aria-label="Dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
      <pre className="mt-2 max-h-40 overflow-y-auto text-[12px] font-mono text-amber-900 whitespace-pre-wrap">{text}</pre>
    </div>
  )
}

export default function UsersPanel({
  currentUser,
  users,
  organizations,
  branches,
  onAddUser,
  onAssignUser,
  onDeactivateUser,
  onReactivateUser,
  onResetPassword,
  onDeleteUser,
  enableTable = false,
  onManageOrg,
  onSetUserRole,
  onSetUsersActive,
  onResetPasswords,
  onRefetchUsers,
}) {
  const [showCreate, setShowCreate] = useState(false)
  const [assigning, setAssigning] = useState(null) // user being assigned
  const [viewMode, setViewMode] = useState(() => {
    const v = localStorage.getItem('pt.usersView')
    return v === 'cards' || v === 'chart' ? v : 'table'
  })
  const [groupByOrg, setGroupByOrg] = useState(() => localStorage.getItem('pt.usersGroupByOrg') === '1')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sort, setSort] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pt.usersSort')) } catch { return null }
  })
  const [page, setPage] = useState(0)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkPasswords, setBulkPasswords] = useState(null)
  const [bulkError, setBulkError] = useState('')

  const isSuperAdmin = currentUser.role === 'superadmin'
  const isAdmin = currentUser.role === 'admin'

  // Debounce the search box so typing doesn't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => clearTimeout(t)
  }, [search])

  // Any change to what's being listed invalidates the current page number.
  useEffect(() => { setPage(0) }, [debouncedSearch, sort?.key, sort?.dir, groupByOrg])

  const tableActive = enableTable && viewMode === 'table'
  const chartActive = enableTable && isSuperAdmin && viewMode === 'chart'
  const directory = useUserDirectory(currentUser, {
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    sort,
    groupByOrg,
    enabled: tableActive,
  })
  const counts = useUserCounts(currentUser, { enabled: enableTable })

  // Server mode needs the user_directory view (users_directory_migration.sql).
  // Without it we keep the original client-side behaviour over the full list.
  const serverMode = tableActive && directory.available

  function switchView(mode) {
    setViewMode(mode)
    setSelectedIds(new Set())
    localStorage.setItem('pt.usersView', mode)
  }

  function toggleGroupByOrg() {
    setGroupByOrg((prev) => {
      localStorage.setItem('pt.usersGroupByOrg', prev ? '0' : '1')
      return !prev
    })
  }

  function changeSort(next) {
    setSort(next)
    localStorage.setItem('pt.usersSort', JSON.stringify(next))
  }

  // Filter visible users based on role
  let visibleUsers = users.filter((u) => u.id !== currentUser.id)
  if (isAdmin) {
    visibleUsers = visibleUsers.filter((u) => u.organizationId === currentUser.organizationId)
  }

  // Sort: admins first, then managers, then staff, then by name
  const roleOrder = { superadmin: 0, admin: 1, manager: 2, staff: 3 }
  visibleUsers = [...visibleUsers].sort(
    (a, b) => roleOrder[a.role] - roleOrder[b.role] || a.name.localeCompare(b.name)
  )

  // Client-side search, used by the fallback path only — in server mode the
  // query does the filtering.
  const query = debouncedSearch.toLowerCase()
  const orgName = (id) => organizations.find((o) => o.id === id)?.name || ''
  const clientSearched = !query ? visibleUsers : visibleUsers.filter((u) =>
    [u.name, u.username, u.email || '', orgName(u.organizationId)]
      .some((field) => field.toLowerCase().includes(query))
  )

  // Server rows arrive without the panel's self-exclusion, so apply it here too.
  const tableRows = serverMode
    ? directory.rows.filter((u) => u.id !== currentUser.id)
    : clientSearched

  const totalAccounts = counts.available
    ? Math.max(0, counts.totals.total - 1) // exclude the signed-in operator
    : visibleUsers.length
  const orgCount = counts.available
    ? [...counts.byOrg.keys()].filter((k) => k !== 'none').length
    : new Set(visibleUsers.map((u) => u.organizationId).filter(Boolean)).size

  const canCreate = isSuperAdmin || isAdmin
  const canManage = (user) =>
    isSuperAdmin ||
    (isAdmin && ['manager', 'staff'].includes(user.role) && user.organizationId === currentUser.organizationId)

  const title = isSuperAdmin ? 'All Users' : 'My Team'
  const selectable = enableTable && isSuperAdmin
  const pageCount = serverMode ? Math.max(1, Math.ceil(directory.total / PAGE_SIZE)) : 1
  const rangeStart = serverMode ? page * PAGE_SIZE + 1 : 1
  const rangeEnd = serverMode ? Math.min((page + 1) * PAGE_SIZE, directory.total) : tableRows.length

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll(ids, on) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  async function refreshAfterBulk() {
    setSelectedIds(new Set())
    directory.refetch()
    counts.refetch()
    onRefetchUsers?.()
  }

  async function runBulkActive(isActive) {
    if (bulkBusy) return
    setBulkBusy(true)
    setBulkError('')
    const result = await onSetUsersActive([...selectedIds], isActive)
    setBulkBusy(false)
    if (result?.success === false) {
      setBulkError(result.message || 'Bulk update failed.')
      return
    }
    await refreshAfterBulk()
  }

  async function runBulkReset() {
    if (bulkBusy) return
    setBulkBusy(true)
    setBulkError('')
    const ids = [...selectedIds]
    const result = await onResetPasswords(ids)
    setBulkBusy(false)
    if (result?.passwords?.length) {
      const byId = new Map(tableRows.map((u) => [u.id, u]))
      setBulkPasswords(result.passwords.map((p) => ({
        username: byId.get(p.id)?.username || p.id,
        tempPassword: p.tempPassword,
      })))
    }
    if (result?.failed) setBulkError(`${result.failed} reset${result.failed !== 1 ? 's' : ''} failed.`)
    await refreshAfterBulk()
  }

  const cardList = visibleUsers.length === 0 ? (
    <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
      <p className="text-gray-400 text-sm">No users yet.</p>
    </div>
  ) : (
    <div className="space-y-2">
      {visibleUsers.map((user) => (
        <UserCard
          key={user.id}
          user={user}
          currentUser={currentUser}
          organizations={organizations}
          branches={branches}
          onDeactivate={onDeactivateUser}
          onReactivate={onReactivateUser}
          onResetPassword={onResetPassword}
          onDelete={onDeleteUser}
          onAssign={setAssigning}
          isSuperAdmin={isSuperAdmin}
          canManage={canManage(user)}
        />
      ))}
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-bold text-gray-800">{title}</h2>
          <p className="text-xs text-gray-400">
            {totalAccounts} account{totalAccounts !== 1 ? 's' : ''}
            {isSuperAdmin && orgCount > 0 && ` across ${orgCount} organisation${orgCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {tableActive && (
            <div className="hidden md:flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-teal-400/40">
              <Search className="w-4 h-4 text-gray-400 flex-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, org…"
                className="w-40 text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent"
              />
            </div>
          )}
          {tableActive && (
            <button
              onClick={toggleGroupByOrg}
              className={`hidden md:flex items-center gap-1.5 text-xs font-semibold px-3 py-2.5 rounded-xl border transition ${
                groupByOrg
                  ? 'bg-teal-50 border-teal-300 text-teal-700'
                  : 'bg-white border-gray-200 text-gray-500 hover:text-gray-700'
              }`}
            >
              <Building2 className="w-3.5 h-3.5" />
              Group by org
            </button>
          )}
          {enableTable && (
            <div className="hidden md:flex bg-gray-100 border border-gray-200 rounded-xl p-0.5 text-xs font-semibold">
              <button
                onClick={() => switchView('table')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-[10px] transition ${
                  viewMode === 'table' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500'
                }`}
              >
                <List className="w-3.5 h-3.5" /> Table
              </button>
              <button
                onClick={() => switchView('cards')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-[10px] transition ${
                  viewMode === 'cards' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500'
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Cards
              </button>
              {isSuperAdmin && (
                <button
                  onClick={() => switchView('chart')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-[10px] transition ${
                    viewMode === 'chart' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  <Network className="w-3.5 h-3.5" /> Chart
                </button>
              )}
            </div>
          )}
          {canCreate && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center space-x-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-3 py-2 rounded-xl transition"
            >
              <Plus className="w-4 h-4" />
              <span>Add User</span>
            </button>
          )}
        </div>
      </div>

      {tableActive && selectedIds.size > 0 && (
        <div className="hidden md:flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl px-3.5 py-2.5">
          <p className="text-[13px] font-semibold text-teal-800">
            {selectedIds.size} selected
          </p>
          <div className="flex items-center gap-1.5 ml-auto">
            <button onClick={() => runBulkActive(false)} disabled={bulkBusy}
              className="flex items-center gap-1.5 text-xs font-semibold text-red-600 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 hover:border-red-300 disabled:opacity-50 transition">
              <UserX className="w-3.5 h-3.5" /> Deactivate
            </button>
            <button onClick={() => runBulkActive(true)} disabled={bulkBusy}
              className="flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 hover:border-green-300 disabled:opacity-50 transition">
              <UserCheck className="w-3.5 h-3.5" /> Reactivate
            </button>
            <button onClick={runBulkReset} disabled={bulkBusy}
              className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 hover:border-blue-300 disabled:opacity-50 transition">
              <RefreshCw className={`w-3.5 h-3.5 ${bulkBusy ? 'animate-spin' : ''}`} /> Reset passwords
            </button>
            <button onClick={() => setSelectedIds(new Set())} disabled={bulkBusy}
              className="text-xs font-semibold text-gray-500 px-2 py-1.5 hover:text-gray-700 disabled:opacity-50">
              Clear
            </button>
          </div>
        </div>
      )}

      {bulkError && (
        <p className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">{bulkError}</p>
      )}

      {bulkPasswords && (
        <BulkPasswords entries={bulkPasswords} onDismiss={() => setBulkPasswords(null)} />
      )}

      {chartActive ? (
        <RoleOrganogram users={users} organizations={organizations} branches={branches} />
      ) : tableActive ? (
        <>
          {/* Tables fight small screens — phones always get the card list. */}
          <div className="hidden md:block space-y-3">
            <UsersTable
              users={tableRows}
              organizations={organizations}
              branches={branches}
              groupByOrg={groupByOrg}
              searchActive={query.length > 0}
              canManage={canManage}
              isSuperAdmin={isSuperAdmin}
              onAssign={setAssigning}
              onDeactivate={onDeactivateUser}
              onReactivate={onReactivateUser}
              onResetPassword={onResetPassword}
              onDelete={onDeleteUser}
              onManageOrg={onManageOrg}
              onSetRole={onSetUserRole}
              sort={sort}
              onSortChange={changeSort}
              clientSort={!serverMode}
              selectable={selectable}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
            />

            {serverMode && directory.total > 0 && (
              <div className="flex items-center gap-3 px-1">
                <p className="text-xs text-gray-400">
                  Showing {rangeStart}–{rangeEnd} of {directory.total}
                  {directory.loading && ' · loading…'}
                </p>
                {pageCount > 1 && (
                  <div className="flex items-center gap-1.5 ml-auto">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0 || directory.loading}
                      className="flex items-center gap-1 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 disabled:opacity-40 hover:border-gray-300 transition"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" /> Prev
                    </button>
                    <span className="text-xs font-semibold text-gray-500 tabular-nums">
                      Page {page + 1} of {pageCount}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                      disabled={page >= pageCount - 1 || directory.loading}
                      className="flex items-center gap-1 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 disabled:opacity-40 hover:border-gray-300 transition"
                    >
                      Next <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="md:hidden space-y-3">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-teal-400/40">
              <Search className="w-4 h-4 text-gray-400 flex-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, org…"
                className="flex-1 min-w-0 text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent"
              />
              {search && (
                <button onClick={() => setSearch('')} aria-label="Clear search" className="text-gray-400">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {clientSearched.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
                <p className="text-gray-400 text-sm">
                  {query ? 'No users match your search.' : 'No users yet.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {clientSearched.map((user) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    currentUser={currentUser}
                    organizations={organizations}
                    branches={branches}
                    onDeactivate={onDeactivateUser}
                    onReactivate={onReactivateUser}
                    onResetPassword={onResetPassword}
                    onDelete={onDeleteUser}
                    onAssign={setAssigning}
                    isSuperAdmin={isSuperAdmin}
                    canManage={canManage(user)}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        cardList
      )}

      {showCreate && (
        <CreateUserModal
          currentUser={currentUser}
          organizations={organizations}
          branches={branches}
          onAddUser={onAddUser}
          onClose={() => setShowCreate(false)}
        />
      )}

      {assigning && (
        <AssignBranchModal
          user={assigning}
          currentUser={currentUser}
          organizations={organizations}
          branches={branches}
          onAssign={onAssignUser}
          onClose={() => setAssigning(null)}
        />
      )}
    </div>
  )
}
